import type { TFn, TaxList, TaxValue } from "../lib/taxonomyTypes";
import { taxonomyValueState } from "../lib/taxonomyHelpers";

// 05m step C §5 — the re-pick signal.
//
// A stored value the vocabulary no longer contains is the failure this whole
// slice exists to prevent, and prod carries one: `Case.stages[].type =
// "Memo, legal opinion"` (census, 2026-08-03), an entry renamed after a case
// was saved. Before this it rendered as ordinary text, indistinguishable from
// a healthy stage — the record looked fine and the link to its entry was gone.
//
// WHY A MARK BESIDE THE VALUE, and not an em-dash replacing it.
//   Slice 20 §3 ruled the reversibility this follows: a row holding something
//   the taxonomy cannot resolve keeps showing WHAT IT HOLDS, muted, and
//   re-creating a matching entry lights it back up. An em-dash would hide the
//   only surviving evidence of what the record used to say, which is exactly
//   what someone re-picking needs to read. `provisionalLevel` (utils.ts) made
//   the same choice for `item_level` and shows the code.
//
//   It is additive on purpose: it renders nothing at all when the value
//   resolves, so a screen with no orphans is byte-identical to before. That
//   matters here because no browser has run.
//
// TWO STATES, ONE MARK. An orphan matches NOTHING; an ambiguous value matches
// TWO DIFFERENT entries — prod's `person_title` "Mr." is `mr`'s English label
// and `me`'s Dutch one, and `me` is Maitre/Meester. Both need the same action
// from the same person (re-pick it) and both must stop the app translating a
// value it cannot read, so they share a mark and differ only in what they say.
// It decides its own state, so no call site can forget to ask.
//
// Module scope, never inside a render body — scripts/lint-inline-components.js
// fails CI on the other shape, and remounting this on every parent render
// would drop the title tooltip mid-hover.

type Props = { value: TaxValue; entries: TaxList; t: TFn };

export default function TaxonomyOrphanMark({ value, entries, t }: Props) {
  const state = taxonomyValueState(value, entries);
  if (state !== "unresolved" && state !== "ambiguous") return null;
  const msg =
    state === "ambiguous"
      ? t("taxonomy.ambiguous.hint", {
          fallback:
            "This value matches more than one entry in the list, so it cannot be shown in another language. Pick the one you mean.",
        })
      : t("taxonomy.orphan.hint", {
          fallback:
            "This entry no longer exists in the list — the value shown is what the record still holds. Pick a current one to relink it.",
        });
  return (
    <span
      style={{ color: "var(--tm)", marginLeft: 4, cursor: "help" }}
      title={msg}
      aria-label={msg}
    >
      ⚠
    </span>
  );
}
